import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOAuth } from '../context/OAuthContext';
import CharacterSelection from '../components/oauth/CharacterSelection';
import './OAuthCallback.css';

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleOAuthCallback, currentUser, logout } = useOAuth();
  const [loading, setLoading] = useState(true);
  const [needsCharacterSelection, setNeedsCharacterSelection] = useState(false);
  const [error, setError] = useState(null);
  const processedRef = useRef(false);

  useEffect(() => {
    const forceCharacterSelection = searchParams.get('forceCharacterSelection');

    if (forceCharacterSelection === 'true' && currentUser) {
      const hasActiveCharacter = (currentUser.activeCharacterId !== null && currentUser.activeCharacterId !== undefined) ||
                                (currentUser.characters && currentUser.characters.some(c => c.isActive === true));

      if (!hasActiveCharacter && currentUser.characters && currentUser.characters.length > 0) {
        console.log('Force character selection triggered');
        setNeedsCharacterSelection(true);
        setLoading(false);
        return;
      }
    }

    if (currentUser && !processedRef.current) {
      const activeCharacterExists = currentUser.activeCharacterId !== null &&
                                   currentUser.activeCharacterId !== undefined &&
                                   currentUser.characters &&
                                   currentUser.characters.some(c => c.id === currentUser.activeCharacterId);
      const hasActiveCharacterFlag = currentUser.characters &&
                                    currentUser.characters.some(c => c.isActive === true || c.isActive === 1);
      const hasActiveCharacter = activeCharacterExists || hasActiveCharacterFlag;
      const needsSelection = currentUser.characters && currentUser.characters.length > 0 && !hasActiveCharacter;

      if (needsSelection) {
        console.log('Current user needs character selection');
        setNeedsCharacterSelection(true);
        setLoading(false);
        return;
      }
    }

    if (processedRef.current) {
      return;
    }

    const processCallback = async () => {
      const code = searchParams.get('code');

      if (!code) {
        if (forceCharacterSelection !== 'true') {
          setError('No authorization code received');
          setLoading(false);
        }
        return;
      }

      processedRef.current = true;

      try {
        const result = await handleOAuthCallback(code);

        if (result.success) {
          const user = JSON.parse(localStorage.getItem('oauth_user') || '{}');

          const activeCharacterExists = user.activeCharacterId !== null &&
                                       user.activeCharacterId !== undefined &&
                                       user.characters &&
                                       user.characters.some(c => c.id === user.activeCharacterId);

          const hasActiveCharacterFlag = user.characters &&
                                        user.characters.some(c => c.isActive === true || c.isActive === 1);

          const hasActiveCharacter = activeCharacterExists || hasActiveCharacterFlag;
          const needsSelection = user.characters && user.characters.length > 0 && !hasActiveCharacter;

          console.log('OAuth callback result check:', {
            resultNeedsSelection: result.needsCharacterSelection,
            needsSelection,
            hasActiveCharacter,
            activeCharacterId: user.activeCharacterId,
            charactersCount: user.characters?.length,
            characters: user.characters?.map(c => ({ id: c.id, isActive: c.isActive }))
          });

          if (result.needsCharacterSelection || needsSelection) {
            setNeedsCharacterSelection(true);
            setLoading(false);
          } else {
            const redirectUrl = sessionStorage.getItem('oauth_redirect_url');
            sessionStorage.removeItem('oauth_redirect_url');

            let targetPath = '/account';

            if (redirectUrl) {
              targetPath = redirectUrl;
            } else {
              if (user.roleId && user.permissions) {
                targetPath = '/admin';
              }
            }

            navigate(targetPath);
          }
        } else {
          setError('Failed to complete authentication');
          setLoading(false);
        }
      } catch (err) {
        setError(err.message || 'Authentication error');
        setLoading(false);
      }
    };

    processCallback();
  }, [searchParams, currentUser]);

  useEffect(() => {
    if (currentUser && !loading && !error) {
      const activeCharacterExists = currentUser.activeCharacterId !== null &&
                                   currentUser.activeCharacterId !== undefined &&
                                   currentUser.characters &&
                                   currentUser.characters.some(c => c.id === currentUser.activeCharacterId);
      const hasActiveCharacterFlag = currentUser.characters &&
                                    currentUser.characters.some(c => c.isActive === true || c.isActive === 1);
      const hasActiveCharacter = activeCharacterExists || hasActiveCharacterFlag;
      const needsSelection = currentUser.characters && currentUser.characters.length > 0 && !hasActiveCharacter;

      if (needsSelection && !needsCharacterSelection) {
        console.log('Detected missing active character, showing selection');
        setNeedsCharacterSelection(true);
      }
    }
  }, [currentUser, loading, error, needsCharacterSelection]);

  const handleCharacterSelected = () => {
    const redirectUrl = sessionStorage.getItem('oauth_redirect_url');
    sessionStorage.removeItem('oauth_redirect_url');

    let targetPath = '/account';

    if (redirectUrl) {
      targetPath = redirectUrl;
    }

    navigate(targetPath);
  };

  if (loading) {
    return (
      <div className="oauth-callback-page">
        <div className="oauth-callback-container">
          <h1>Completing Authentication...</h1>
          <p>Please wait while we verify your account.</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="oauth-callback-page">
        <div className="oauth-callback-container">
          <h1>Authentication Error</h1>
          <p>{error}</p>
          <button onClick={() => navigate('/account')} className="oauth-button">
            Go to Account
          </button>
        </div>
      </div>

    );
  }
  if (needsCharacterSelection && currentUser) {
    const handleLogout = () => {
      logout();
      navigate('/');
    };
    return (
      <div className="oauth-callback-page">
        <div className="oauth-callback-container">
          <h1>Select Your Character</h1>
          <p>Please select which character you'd like to use for your account. You must select a character to continue.</p>
          <CharacterSelection
            characters={currentUser.characters || []}
            onCharacterSelected={handleCharacterSelected}
          />
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <button onClick={handleLogout} className="oauth-button" style={{ background: '#666', marginTop: '1rem' }}>
              Logout Instead
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default OAuthCallback;

